import { RefObject, useCallback, useEffect } from 'react'

import { injectIframeScript } from '../utils/injectIframeScript'

async function sha256(message: string) {
  const encoder = new TextEncoder()
  const data = encoder.encode(message)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

export const useInjectIframeScript = (
  iframeRef: RefObject<HTMLIFrameElement>,
  script: string
) => {
  const handleInject = useCallback(async () => {
    if (!iframeRef.current) return

    const iframeDoc = iframeRef.current.contentDocument
    if (!iframeDoc) return

    // don't inject scripts into about:blank pages. safari fires the `load` event
    // even for the initial blank page, and that causes an infinite reload loop.
    if (iframeDoc.URL === 'about:blank') return

    const scriptHash = await sha256(script)

    // Check if this specific script ID already exists
    const existingScript = iframeDoc.querySelector(
      `script[data-container-hash="${scriptHash}"]`
    )
    if (existingScript) {
      return
    }

    injectIframeScript(iframeRef.current, script, scriptHash)
  }, [iframeRef, script])

  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe) {
      return
    }

    iframe.addEventListener('load', handleInject)

    if (iframe.contentDocument?.readyState === 'complete') {
      handleInject()
    }

    return () => {
      iframe.removeEventListener('load', handleInject)
    }
  }, [iframeRef, handleInject])
}
